/**
 * VesselMarker · CC-ICON-001 — Cruise Command Vessel Marker
 *
 * **Status:** RATIFIED · v1.0 FROZEN · PO 2026-08-04. Canonical variant = "h" (Heading C).
 * Governed asset. See /app/docs/governance/design/CC-ICON-001-vessel-marker.md.
 *
 * The marker is a *navigation variant of the Cruise Command "C" logo*,
 * not a new ship silhouette. The mark represents "my ship" — the same
 * relationship Google Maps' blue dot has with the user's position. The
 * ship-silhouette exploration (v1) is archived under
 * /app/frontend/src/assets/vessel-markers/_archive_ship_silhouette_*.
 *
 * The remaining variants (a–g, i, j) stay in the file for one release
 * cycle as reference. After that they will be removed and only the
 * canonical variant will ship.
 *
 * Anatomy inherited from the brand logo:
 *   • Bold "C" opening — brand primitive (rotated -90° so mouth = north)
 *   • Interior compass wedge · gold NE↔SW axis — brand signature
 *   • Palette: navy #0F2244 · gold #C28E42 · white
 *
 * Canonical geometry (v1.0 · frozen):
 *   • Outer radius     11    (was 10 in the exploration; +10%)
 *   • Thickness         3.8  (was 3.2; borrowed from concept B)
 *   • Opening angle    60°   (preserved · brand geometry)
 *   • Wedge size        3.8  (was 3.0 → 3.4 → 3.8 final polish · +12%)
 *   • Disc stroke       1.15 (was 1.4;  ~18% lighter outer circle)
 *
 * Motion capabilities (hooks present, animations NOT wired here):
 *   • `heading` prop rotates the inner group for live vessel bearing.
 *   • `pulse`   prop reserved for the current-position pulse animation.
 *
 * See /pages/VesselMarkerPreview.jsx for the design-review surface.
 */
import React from "react";

// ─── Brand tokens · CC-ICON-001 · locked at ratification (2026-08-04) ─
// These MUST match /app/frontend/src/index.css --cc-vessel-* tokens.
// Change here only in coordination with a governed design revision.
const NAVY  = "#0F2244";
const GOLD  = "#C28E42";
const WHITE = "#FFFFFF";

// ─── Geometry helpers ────────────────────────────────────────────────
// All concepts share the 32×32 canvas + disc center (16, 16).
// The optical centering offset accounts for the C's missing right side
// — nudge the C ~0.6px left so it *reads* centered inside the disc.
const CX = 16;
const CY = 16;
const OPTICAL_OFFSET_X = -0.6;

/**
 * Build a "C" ring path (open on the right).
 *   R      outer radius
 *   T      thickness (T < R)
 *   theta  half of the opening angle in degrees (opening = 2·theta)
 * Optical centering applied by shifting the whole ring's cx.
 */
function cPath(R, T, theta) {
  const r = R - T;
  const rad = (theta * Math.PI) / 180;
  const cx = CX + OPTICAL_OFFSET_X;
  const cy = CY;
  const ox1 = cx + R * Math.cos(rad);
  const oy1 = cy - R * Math.sin(rad);
  const ox2 = cx + R * Math.cos(-rad);
  const oy2 = cy - R * Math.sin(-rad);
  const ix1 = cx + r * Math.cos(rad);
  const iy1 = cy - r * Math.sin(rad);
  const ix2 = cx + r * Math.cos(-rad);
  const iy2 = cy - r * Math.sin(-rad);
  // Outer arc (long way, counter-clockwise) then straight to inner,
  // inner arc (long way, clockwise) back to close.
  return (
    `M ${ox1.toFixed(2)} ${oy1.toFixed(2)} ` +
    `A ${R} ${R} 0 1 0 ${ox2.toFixed(2)} ${oy2.toFixed(2)} ` +
    `L ${ix2.toFixed(2)} ${iy2.toFixed(2)} ` +
    `A ${r} ${r} 0 1 1 ${ix1.toFixed(2)} ${iy1.toFixed(2)} Z`
  );
}

/**
 * Compass wedge — the interior 4-triangle X from the brand logo,
 * scaled and centered. Two gold triangles on the NE↔SW diagonal,
 * two navy on NW↔SE. Includes a tiny center dot ("pivot").
 *
 * `size` controls the wedge's radius. Set fill overrides to render
 * an inverted-disc variant (gold+white pair rather than gold+navy).
 */
function CompassWedge({ x = 17.6, y = 16, size = 3.4, minor = MINOR_FALLBACK }) {
  // Diagonal offsets (45°)
  const d = size;
  const dd = size * 0.32; // waist thickness
  return (
    <g>
      {/* NE↔SW gold pair */}
      <path
        d={`M ${x + d} ${y - d} L ${x + dd} ${y - dd} L ${x - d} ${y + d} L ${x - dd} ${y + dd} Z`}
        fill={GOLD}
      />
      {/* NW↔SE minor pair (navy on light disc; white on dark disc) */}
      <path
        d={`M ${x - d} ${y - d} L ${x - dd} ${y - dd} L ${x + d} ${y + d} L ${x + dd} ${y + dd} Z`}
        fill={minor}
      />
      {/* Center pivot */}
      <circle cx={x} cy={y} r={size * 0.22} fill={GOLD} />
    </g>
  );
}
const MINOR_FALLBACK = NAVY;

// ─── Shared shadow filter ────────────────────────────────────────────
function ShadowDefs({ uid, strength = 0.22 }) {
  return (
    <defs>
      <filter id={`cc-vsl-shadow-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
        <feDropShadow dx="0" dy="0.6" stdDeviation="0.8" floodColor="#0F172A" floodOpacity={strength} />
      </filter>
    </defs>
  );
}

// ─── Concept A · Faithful C ──────────────────────────────────────────
function VariantA({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 3.2, 30)} fill={NAVY} />
        <CompassWedge size={3.2} />
      </g>
    </>
  );
}

// ─── Concept B · Thickened C ─────────────────────────────────────────
function VariantB({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 4.0, 25)} fill={NAVY} />
        <CompassWedge size={2.9} />
      </g>
    </>
  );
}

// ─── Concept C · Prominent Wedge ─────────────────────────────────────
function VariantC({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 2.6, 30)} fill={NAVY} />
        <CompassWedge size={4.4} />
      </g>
    </>
  );
}

// ─── Concept D · Solid C + gold dot ──────────────────────────────────
function VariantD({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 3.4, 32)} fill={NAVY} />
        <circle cx={CX + OPTICAL_OFFSET_X + 3.0} cy={CY} r="1.6" fill={GOLD} />
      </g>
    </>
  );
}

// ─── Concept E · Pure C (no wedge) ───────────────────────────────────
function VariantE({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 3.6, 30)} fill={NAVY} />
      </g>
    </>
  );
}

// ─── Concept F · Compass-Ring C ──────────────────────────────────────
// Adds four faint cardinal ticks on the outer disc (never rotate) so
// the marker doubles as a subtle instrument reference.
function VariantF({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <g opacity="0.5">
        <line x1={CX} y1="3.6"  x2={CX} y2="5.2"  stroke={NAVY} strokeWidth="0.8" strokeLinecap="round" />
        <line x1={CX} y1="26.8" x2={CX} y2="28.4" stroke={NAVY} strokeWidth="0.8" strokeLinecap="round" />
        <line x1="3.6"  y1={CY} x2="5.2"  y2={CY} stroke={NAVY} strokeWidth="0.8" strokeLinecap="round" />
        <line x1="26.8" y1={CY} x2="28.4" y2={CY} stroke={NAVY} strokeWidth="0.8" strokeLinecap="round" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(9.4, 3.0, 30)} fill={NAVY} />
        <CompassWedge size={3.0} x={17.0} />
      </g>
    </>
  );
}

// ─── Concept G · Inverted disc (Navy face) ───────────────────────────
// Reverses the palette — navy disc, white C, gold+white compass. Reads
// stronger on light map tiles where the standard variant can wash out.
function VariantG({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} strength={0.28} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.8" fill={NAVY} />
      </g>
      <g className="cc-vessel">
        <path d={cPath(10, 3.2, 30)} fill={WHITE} />
        <CompassWedge size={3.2} minor={WHITE} />
      </g>
    </>
  );
}

// ─── Concept H · Heading C · CC-ICON-001 v1.0 (FROZEN · PO 2026-08-04) ─
// PO-approved canonical variant. Refined from the initial exploration
// per the ratification brief plus one final polish (2026-08-04):
//   • C outer radius       10 → 11    (+10%)
//   • C thickness          3.2 → 3.8  (borrowed from B; holds at 24px)
//   • Wedge size           3.0 → 3.4 → 3.8  (+13%, then +12% final polish)
//   • Disc stroke width    1.4 → 1.15 (~18% lighter outer circle)
//   • Opening angle        60° preserved (brand geometry)
//   • Wedge angle/placement preserved (final polish is size only)
// The C mouth points north; apply rotate() to the .cc-vessel group for
// live heading tracking. The compass wedge stays cardinal (NE↔SW gold)
// regardless of heading so the traveler always sees the brand
// signature in its ratified orientation.
function VariantH({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.6" fill={WHITE} stroke={NAVY} strokeWidth="1.15" />
      </g>
      <g className="cc-vessel" style={{ transformOrigin: "16px 16px" }}>
        {/* C mouth up. Base cPath opens right; rotate -90° so opening = north. */}
        <g transform="rotate(-90 16 16)">
          <path d={cPath(11, 3.8, 30)} fill={NAVY} />
        </g>
        {/* Cardinal compass wedge — never rotates with the C.
            Final-polish wedge size = 3.8 (was 3.4). Angle and
            placement unchanged from the ratified geometry. */}
        <CompassWedge size={3.8} x={16} />
      </g>
    </>
  );
}

// ─── Concept I · Radar-Halo C ────────────────────────────────────────
// Adds an outer navy hairline ring (with a 1.5px gap from the main
// stroke) that reads as a radar sweep silhouette — visually primes the
// eye for the future "current position" pulse animation.
function VariantI({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.5" fill={WHITE} stroke={NAVY} strokeWidth="1.4" />
      </g>
      <circle cx={CX} cy={CY} r="15.1" fill="none" stroke={NAVY} strokeWidth="0.5" opacity="0.35" />
      <g className="cc-vessel">
        <path d={cPath(10, 3.2, 30)} fill={NAVY} />
        <CompassWedge size={3.2} />
      </g>
    </>
  );
}

// ─── Concept J · Editorial C ─────────────────────────────────────────
// The most refined variant — thinner disc border, slimmer C, wedge
// only (no C+wedge co-occupancy). Reads as premium/editorial at the
// cost of some legibility at 24px.
function VariantJ({ uid }) {
  return (
    <>
      <ShadowDefs uid={uid} strength={0.18} />
      <g filter={`url(#cc-vsl-shadow-${uid})`}>
        <circle cx={CX} cy={CY} r="13.6" fill={WHITE} stroke={NAVY} strokeWidth="0.9" />
      </g>
      <g className="cc-vessel">
        <path d={cPath(9.4, 2.4, 34)} fill={NAVY} />
        <CompassWedge size={2.6} x={17.0} />
      </g>
    </>
  );
}

const RENDERERS = {
  a: VariantA, b: VariantB, c: VariantC, d: VariantD, e: VariantE,
  f: VariantF, g: VariantG, h: VariantH, i: VariantI, j: VariantJ,
};

export const CANONICAL_VARIANT = "h";

export const VESSEL_MARKER_VARIANTS = [
  { id: "a", name: "Faithful C",         rationale: "Baseline. Logo proportions transposed into the map-pin chassis. Preserves the brand mark's rhythm; C thickness = 3.2, opening = 60°." },
  { id: "b", name: "Thickened C",        rationale: "+25% stroke weight, narrower mouth (50° opening). C becomes more decisive and holds shape down to 24px." },
  { id: "c", name: "Prominent Wedge",    rationale: "Compass wedge scaled up 1.4× while the C thins to give it space. Brand signature leads; C recedes into a supporting role." },
  { id: "d", name: "Solid C + Gold Dot", rationale: "Ultra-simplified. Wedge replaced by a single gold dot on the C's midline. Optimizes for pure map legibility at any size." },
  { id: "e", name: "Pure C",             rationale: "Wedge removed entirely. Tests whether the C alone carries brand recognition. Most Apple/Find-My in tone." },
  { id: "f", name: "Compass-Ring C",     rationale: "Adds four faint cardinal ticks (N/E/S/W) on the disc, never rotating. The marker becomes a subtle navigation instrument in its own right." },
  { id: "g", name: "Inverted Disc",      rationale: "Navy disc / white C / gold wedge. Reads strongest on light map tiles (blue-water backdrops) where the white-disc variant can wash out." },
  { id: "h", name: "Heading C", approved: true, ratification: "CC-ICON-001 v1.0 · Frozen · PO 2026-08-04", rationale: "APPROVED & FROZEN as CC-ICON-001 v1.0. Refined per ratification brief + final polish: R 10→11 (+10%), thickness 3.2→3.8, wedge 3.0→3.8 (+12% final polish on the 3.4 refinement), outer stroke 1.4→1.15. Wedge angle & placement preserved. The C's mouth becomes the bow. Wedge stays cardinal for brand recognition regardless of heading." },
  { id: "i", name: "Radar-Halo C",       rationale: "Outer navy hairline (1.5px gap) primes the eye for the future 'live position' pulse. Static variant reads richer than concept A alone." },
  { id: "j", name: "Editorial C",        rationale: "The most refined variant. Thinner disc border, slimmer C, softer shadow. Premium and quiet — best when the marker sits amongst other UI, not on a busy map." },
];

// ─── Public component ────────────────────────────────────────────────
/**
 * Props:
 *   variant  "a" .. "j"                          — default "a"
 *   size     number (px)                          — default 32
 *   heading  number (deg, 0 = north/up)           — rotates inner group
 *   pulse    boolean                              — adds pulse animation
 *   title    string                               — a11y label
 *   testid   string                               — data-testid override
 */
export default function VesselMarker({
  variant = CANONICAL_VARIANT,
  size = 32,
  heading = 0,
  pulse = false,
  title = "Cruise Command vessel",
  testid,
  className,
  style,
}) {
  const Renderer = RENDERERS[variant] || RENDERERS[CANONICAL_VARIANT];
  const rawId = React.useId();
  const uid = rawId.replace(/[:]/g, "");

  return (
    <span
      className={`cc-vessel-marker ${pulse ? "cc-vessel-marker--pulse" : ""} ${className || ""}`}
      style={{ display: "inline-block", lineHeight: 0, ...style }}
      data-testid={testid || `cc-vessel-marker-${variant}`}
      role="img"
      aria-label={title}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width={size} height={size}>
        <title>{title}</title>
        <g
          style={heading ? { transform: `rotate(${heading}deg)`, transformOrigin: "16px 16px", transformBox: "view-box" } : undefined}
        >
          <Renderer uid={uid} />
        </g>
      </svg>
    </span>
  );
}
